import Stack from '@mui/material/Stack'

import { InstitutionLogo, MerchantLogo } from '@mxenabled/mxui'
import { Icon } from '@mxenabled/mx-icons'

interface DepositLogoProps {
  /** Category guid, required alongside `merchantGuid` for the merchant logo lookup. */
  categoryGuid?: string
  /** Fallback mx-icons ligature glyph for the generic tile when no guid is present. */
  iconName?: string
  /** Institution guid — renders an `InstitutionLogo` when present and no merchant guid. */
  institutionGuid?: string
  /** Institution display name, used as the logo's alt text. */
  institutionName?: string
  /** Merchant guid — renders a `MerchantLogo` when present (highest priority). */
  merchantGuid?: string
  /** Merchant display name, used as the logo's alt text. */
  merchantName?: string
}

const SIZE = 48

/**
 * Square themed tile standing in for a merchant/institution logo when no guid is sent.
 * Drawn with theme colors so it stays correct in dark mode.
 */
const DepositLogoTile = ({ iconName }: { iconName: string }) => (
  <Stack
    sx={{
      alignItems: 'center',
      bgcolor: 'success.main',
      height: '100%',
      justifyContent: 'center',
      width: '100%',
    }}
  >
    <Icon name={iconName} size={24} sx={{ color: 'success.contrastText' }} />
  </Stack>
)

/**
 * Logo shown in a deposit callout. Mirrors the spec's per-variant sourcing: a real
 * merchant logo when a `merchantGuid` is sent (Payroll, Merchant Credit, Payroll Advance),
 * an institution logo for institution-sourced deposits (Check, Large Deposit), and a themed
 * generic tile otherwise (Government, Tax).
 */
export const DepositLogo = ({
  categoryGuid,
  iconName = 'payments',
  institutionGuid,
  institutionName,
  merchantGuid,
  merchantName,
}: DepositLogoProps) => {
  if (merchantGuid) {
    return (
      <MerchantLogo
        alt={merchantName ?? ''}
        categoryGuid={categoryGuid ?? ''}
        merchantGuid={merchantGuid}
        size={SIZE}
      />
    )
  }

  if (institutionGuid) {
    return (
      <InstitutionLogo alt={institutionName ?? ''} institutionGuid={institutionGuid} size={SIZE} />
    )
  }

  return <DepositLogoTile iconName={iconName} />
}

export default DepositLogo
